import { Controller } from "@hotwired/stimulus"
import { RESULTS_FRAME, filterSource } from "../../utils/event_filter_source"

const CANVAS_TIMEOUT = 20000
// tags is repeated and bracketed, as the toolbar checkboxes submit it.
const FILTER_PARAMS = ["range", "from", "to", "lat", "lng", "radius", "tags[]"]
// Mirrors PublicEventFilter::DEFAULT_RADIUS_KM / RADIUS_RANGE so the drawn circle
// matches the radius the server actually filtered by.
const DEFAULT_RADIUS_KM = 25
const MIN_RADIUS_KM = 1
const MAX_RADIUS_KM = 50

export default class extends Controller {
  static targets = ["map", "overlay", "selectionNote"]
  static outlets = ["events-view"]

  connect() {
    this.selectedUids = new Set()
    this.byUid = {}
    this.unmappedCount = 0
    this.bounds = null
    this.home = null
    this.canvasReady = false

    this.labels = this.element.dataset

    this._onFrameLoad = (ev) => {
      if (ev.target && ev.target.id === RESULTS_FRAME) this.refilter()
    }
    document.addEventListener("turbo:frame-load", this._onFrameLoad)

    this._connected = true
    if (this._revealPending) { this._revealPending = false; this.reveal() }
  }

  disconnect() {
    if (this._onCanvasMessage) {
      window.removeEventListener("message", this._onCanvasMessage)
      this._onCanvasMessage = null
    }
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
  }

  // Called by the events-view outlet when the map view is shown. First call
  // lazily mounts the map canvas iframe + fetches the JSON dataset, then boots;
  // later calls just resize + refit the already-built map.
  reveal() {
    // An outlet can call reveal() before this controller's connect() has run
    // (Stimulus outlet-connected can fire first); defer until state/labels exist.
    if (!this._connected) { this._revealPending = true; return }
    if (this.revealed) {
      if (this.canvasReady) {
        this.post({ type: "resize" })
        this.fitAll()
      }
      return
    }
    this.revealed = true
    Promise.all([this.mountCanvas(), this.fetchData()])
      .then(([, data]) => this.boot(data))
      .catch(() => { if (this.hasOverlayTarget) this.overlayTarget.textContent = this.label("mapErrorLabel", "Could not load the map.") })
  }

  // The Google Maps JS API is never loaded on the club domain: it lives inside
  // this iframe, served from the maps-embed subdomain the API key is HTTP-referrer
  // restricted to. We keep the data and list here and drive the frame over postMessage.
  mountCanvas() {
    if (this._canvasPromise) return this._canvasPromise
    const url = this.label("canvasUrl", "")
    if (!url) return Promise.reject(new Error("Missing map canvas URL"))
    this.canvasOrigin = new URL(url, window.location.href).origin
    this._canvasPromise = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Map canvas did not load")), CANVAS_TIMEOUT)
      this._onCanvasMessage = (ev) => {
        if (ev.origin !== this.canvasOrigin) return
        if (!this.frame || ev.source !== this.frame.contentWindow) return
        const msg = ev.data
        if (!msg || msg.em !== 1 || typeof msg.type !== "string") return
        if (msg.type === "ready") {
          clearTimeout(timer)
          this.canvasReady = true
          this.syncOverlay()
          resolve()
        } else if (msg.type === "error") {
          clearTimeout(timer)
          reject(new Error("Map canvas failed to load"))
        } else {
          this.onCanvasMessage(msg)
        }
      }
      window.addEventListener("message", this._onCanvasMessage)
      const frame = document.createElement("iframe")
      frame.className = "absolute inset-0 h-full w-full border-0"
      frame.title = this.label("mapLabel", "Map")
      frame.src = url
      this.frame = frame
      this.mapTarget.appendChild(frame)
    })
    return this._canvasPromise
  }

  post(msg) {
    if (!this.frame || !this.frame.contentWindow || !this.canvasOrigin) return
    this.frame.contentWindow.postMessage({ em: 1, ...msg }, this.canvasOrigin)
  }

  onCanvasMessage(msg) {
    switch (msg.type) {
      case "bounds":
        // A hidden pane collapses the frame to 0x0 and Maps reports a single
        // point as the viewport; keeping it would empty the list.
        if (!this.mapTarget.offsetWidth || !this.mapTarget.offsetHeight) break
        this.bounds = { sw: msg.sw, ne: msg.ne }
        this.render(true)
        this.list("setViewportBounds", this.bounds)
        break
      case "marker-click": {
        const e = this.byUid[msg.uid]
        if (e) {
          this.list("focusCard", String(msg.uid), false)
          this.openInfoWindow(e)
        }
        break
      }
      case "marker-over":
        this.list("focusCard", String(msg.uid), false)
        break
      case "marker-out":
        this.list("unfocusCard", String(msg.uid))
        break
      case "cluster-click":
        this.selectCluster(msg.uids || [], msg.bounds)
        break
      case "map-click":
        this.closeInfoWindow()
        this.clearSelection()
        break
      case "info-closed":
        this.closeInfoWindow()
        break
    }
  }

  // The feed is filtered server-side by the same params the listing is showing.
  fetchData() {
    const url = new URL(this.label("jsonUrl", "/public/event-map.json"), window.location.href)
    const current = filterSource().searchParams
    FILTER_PARAMS.forEach((key) => {
      current.getAll(key).forEach((value) => {
        if (value) url.searchParams.append(key, value)
      })
    })
    return fetch(url.toString(), { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => ({}))
  }

  boot(data) {
    this.load(data)
    this.list("setCapped", this.isCapped(data))
    this.syncCircle()
    this.render()
    this.fitAll()
    this.syncOverlay()
  }

  isCapped(data) {
    return Boolean(data && data.capped)
  }

  // The feed carries events with no usable coordinates as lat/lng nulls; those
  // have no marker, so only their count travels to the list.
  load(data) {
    const payload = data && typeof data === "object" ? data : {}
    this.home = this.homeCenter(payload.home)
    const parsed = Array.isArray(payload.events) ? payload.events : []
    this.all = parsed.filter((e) => typeof e.lat === "number" && typeof e.lng === "number")
    this.unmappedCount = parsed.length - this.all.length
    this.byUid = {}
    this.all.forEach((e) => { this.byUid[e.uid] = e })
    this.mappableUids = new Set(this.all.map((e) => String(e.uid)))
  }

  homeCenter(home) {
    if (!home || !isFinite(home.lat) || !isFinite(home.lng)) return null
    return { lat: home.lat, lng: home.lng }
  }

  // The viewport narrowing has no URL state, so the count chip is the only way
  // back out of a pan, a zoom or a cluster selection.
  resetArea() {
    this.selectedUids = new Set()
    this.bounds = null
    this.render()
    const fitted = this.fitAll()
    this.list("clearAreaBounds", fitted)
  }

  refilter() {
    if (!this.revealed) return
    this.fetchData().then((data) => {
      this.load(data)
      this.list("setCapped", this.isCapped(data))
      this.syncCircle()
      this.selectedUids = new Set()
      this.bounds = null
      this.render()
      this.fitAll()
    })
  }

  // i18n strings supplied by the view as data-* attributes on the root element.
  label(key, fallback) {
    const v = this.labels[key]
    return (v === undefined || v === null) ? fallback : v
  }

  interp(key, fallback, vars) {
    let out = this.label(key, fallback)
    Object.keys(vars || {}).forEach((k) => { out = out.replace("%{" + k + "}", vars[k]) })
    return out
  }

  list(method, ...args) {
    if (this.hasEventsViewOutlet) this.eventsViewOutlet[method](...args)
  }

  // Replaces LatLngBounds#contains now that the map lives in the frame; the
  // longitude test wraps when the viewport crosses the antimeridian.
  inBounds(e) {
    const { sw, ne } = this.bounds
    if (e.lat < sw.lat || e.lat > ne.lat) return false
    if (sw.lng <= ne.lng) return e.lng >= sw.lng && e.lng <= ne.lng
    return e.lng >= sw.lng || e.lng <= ne.lng
  }

  // The canvas handshake and the JSON fetch resolve independently.
  get ready() {
    return this.canvasReady && Array.isArray(this.all)
  }

  syncOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.toggle("hidden", this.ready)
  }

  // The map plots every event the server returned; the list is refetched from the
  // server for the current viewport, or for a clicked cluster.
  render(viewportOnly) {
    if (!this.ready) return
    if (!viewportOnly) {
      this.selectedUids = new Set()
      // The canvas tracks its own active set, and the cluster renderer reads it
      // as the markers are rebuilt, so clear it there first.
      this.refreshMarkerIcons()
      this.rebuildMarkers(this.all)
    }

    const selectionActive = this.selectedUids.size > 0
    const visible = selectionActive
      ? this.all.filter((e) => this.selectedUids.has(e.uid))
      : (this.bounds ? this.all.filter((e) => this.inBounds(e)) : this.all)

    if (this.hasSelectionNoteTarget) this.selectionNoteTarget.classList.toggle("hidden", !selectionActive)

    const count = visible.length
    const areaKey = selectionActive
      ? (count === 1 ? "headGroupOne" : "headGroupOther")
      : (count === 1 ? "headAreaOne" : "headAreaOther")

    this.list("applyViewport", {
      mappable: this.mappableUids,
      unmappedCount: this.unmappedCount,
      areaText: this.interp(areaKey, "%{count} in view", { count: count.toLocaleString() }),
      narrowed: selectionActive || count < this.all.length,
    })
  }

  // The location filter has no marker of its own, so the searched radius is
  // drawn on the map to explain why the listing is narrowed.
  searchCircle() {
    const p = filterSource().searchParams
    const lat = parseFloat(p.get("lat"))
    const lng = parseFloat(p.get("lng"))
    if (!isFinite(lat) || !isFinite(lng)) return null
    const radius = parseInt(p.get("radius"), 10)
    const radiusKm = Math.min(Math.max(isFinite(radius) ? radius : DEFAULT_RADIUS_KM, MIN_RADIUS_KM), MAX_RADIUS_KM)
    return { center: { lat, lng }, radiusKm }
  }

  syncCircle() {
    this.circle = this.searchCircle()
    if (this.circle) this.post({ type: "circle", center: this.circle.center, radiusKm: this.circle.radiusKm })
    else this.post({ type: "circle-clear" })
  }

  // --- map frame layer -------------------------------------------------------

  pinDate(iso) {
    if (!iso) return "TBC"
    const d = new Date(iso)
    if (isNaN(d)) return "TBC"
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" })
  }

  rebuildMarkers(matched) {
    this.post({
      type: "markers",
      events: matched.map((e) => ({ uid: e.uid, lat: e.lat, lng: e.lng, pin: this.pinDate(e.start) })),
      home: this.home,
    })
  }

  openInfoWindow(e) {
    this._openUid = e.uid
    this.card(String(e.uid)).then((html) => {
      if (this._openUid !== e.uid || !html) return
      this.post({ type: "open-info", uid: e.uid, html: this.infoWindowHtml(html) })
    })
  }

  card(uid) {
    if (!this.hasEventsViewOutlet) return Promise.resolve("")
    return this.eventsViewOutlet.fetchCard(uid)
  }

  closeInfoWindow() {
    const uid = this._openUid
    this._openUid = null
    if (uid) this.unhighlightMarker(uid)
  }

  // The popup renders inside the maps-embed frame, so card URLs have to be
  // absolute against the club origin; the card itself carries target="_blank".
  infoWindowHtml(html) {
    const tmpl = document.createElement("template")
    tmpl.innerHTML = (html || "").trim()
    tmpl.content.querySelectorAll("[href], [src]").forEach((node) => {
      ["href", "src"].forEach((attr) => {
        const value = node.getAttribute(attr)
        if (!value) return
        let absolute = value
        try { absolute = new URL(value, window.location.href).href } catch { /* leave as-is */ }
        node.setAttribute(attr, absolute)
      })
    })
    return tmpl.innerHTML
  }

  selectCluster(uids, bounds) {
    const members = (uids || []).filter((uid) => this.byUid[uid])
    if (!members.length) return
    this.selectedUids = new Set(members)
    this.refreshMarkerIcons()
    this.render(true)
    if (bounds) this.list("setClusterBounds", bounds)
  }

  clearSelection() {
    if (this.selectedUids.size === 0) return
    this.selectedUids = new Set()
    this.refreshMarkerIcons()
    this.render(true)
    this.list("clearClusterBounds")
  }

  refreshMarkerIcons() {
    this.post({ type: "active", uids: Array.from(this.selectedUids).map(String) })
  }

  highlightMarker(uid) {
    this.post({ type: "hover", uid, on: true })
  }

  unhighlightMarker(uid) {
    this.post({ type: "hover", uid, on: false })
  }

  // The mobile pill hides the map behind a class, so a fit queued while it had
  // no size has to replay once the frame is measurable again.
  remeasure() {
    if (!this.revealed || !this.canvasReady) return
    this.post({ type: "resize" })
    if (this._fitPending) { this._fitPending = false; this.fitAll() }
  }

  fitAll() {
    if (!this.ready) return false
    if (!this.mapTarget.offsetWidth || !this.mapTarget.offsetHeight) { this._fitPending = true; return false }
    if (this.circle) { this.post({ type: "fit-circle" }); return true }
    let sw = null
    let ne = null
    this.all.forEach((e) => {
      if (!isFinite(e.lat) || !isFinite(e.lng)) return
      if (!sw) { sw = { lat: e.lat, lng: e.lng }; ne = { lat: e.lat, lng: e.lng }; return }
      sw.lat = Math.min(sw.lat, e.lat)
      sw.lng = Math.min(sw.lng, e.lng)
      ne.lat = Math.max(ne.lat, e.lat)
      ne.lng = Math.max(ne.lng, e.lng)
    })
    this.post({ type: "fit", sw, ne, padding: 40, home: this.home })
    return true
  }
}
